import styled from 'styled-components';

import { IconButtonProps, IconButtonSize } from './IconButton.types';

const getLayout = (size?: IconButtonSize) => {
  if (size === 'medium') return { width: '48px', height: '48px', padding: '12px 16px' };
  if (size === 'small') return { width: '44px', height: '44px', padding: '12px 14px' };
  if (size === 'xs') return { width: '32px', height: '32px', padding: '8px' };

  return { width: '56px', height: '56px', padding: '14px 20px' };
};

export const IconButtonBase = styled.button<IconButtonProps>`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ${({ size }) => getLayout(size)}

  border: none;
  box-sizing: border-box;

  background: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.round};

  //Typography
  ${({ size, theme }) => {
    switch (size) {
      case 'medium':
        return theme.label.md;
      case 'small':
        return theme.label.sm;
      case 'xs':
        return theme.label.xs;
      default:
        return theme.label.lg;
    }
  }}

  &:disabled {
    background: ${({ theme }) => theme.background.disabled};
    color: ${({ theme }) => theme.icon.disabled};
    cursor: default;
  }

  &:hover,
  &.hover {
    cursor: pointer;
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6056%3A25980&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const PrimaryIconButton = styled(IconButtonBase)<IconButtonProps>`
  color: ${({ theme }) => theme.icon.inverse};

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.primaryActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      background: ${({ theme }) => theme.background.primaryHover};
    }
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6056%3A25995&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const PrimaryInverseIconButton = styled(PrimaryIconButton)<IconButtonProps>`
  background: ${({ theme }) => theme.background.default};
  color: ${({ theme }) => theme.icon.primary};

  &:not(:disabled):active {
    color: ${({ theme }) => theme.icon.inverse};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.icon.primaryHover};

      background: ${({ theme }) => theme.background.defaultHover};
    }
  }

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.defaultActive};
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6056%3A25990&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const SecondaryIconButton = styled(IconButtonBase)<IconButtonProps>`
  background: transparent;
  color: ${({ theme }) => theme.icon.strong};
  ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.strong)}

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.icon.inverse};

      background: ${({ theme }) => theme.background.primaryHover};
    }
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.icon.inverse};

    background: ${({ theme }) => theme.background.primaryActive};
  }

  &:disabled {
    color: ${({ theme }) => theme.icon.disabled};
    background: transparent;
    ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.disabled)}
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6056%3A26000&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const SecondaryInverseIconButton = styled(SecondaryIconButton)<IconButtonProps>`
  background: transparent;
  color: ${({ theme }) => theme.icon.inverse};

  ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.inverse)}

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.icon.primaryHover};

      background: ${({ theme }) => theme.background.defaultHover};
    }
  }

  &:disabled {
    color: ${({ theme }) => theme.icon.disabledSubtle};
    background: transparent;
    ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.disabled)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6785%3A25363&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const TertiaryIconButton = styled(IconButtonBase)<IconButtonProps>`
  background: transparent;
  color: ${({ theme }) => theme.icon.default};

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.icon.primaryHover};

      background: ${({ theme }) => theme.background.defaultHover};
    }
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.icon.primaryActive};

    background: ${({ theme }) => theme.background.default};
  }

  &:disabled {
    color: ${({ theme }) => theme.icon.disabled};
    background: transparent;
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6785%3A25368&t=4lZIpgN8pkR08dSe-4 Reference}
 */
export const TertiaryInverseIconButton = styled(TertiaryIconButton)<IconButtonProps>`
  background: transparent;
  color: ${({ theme }) => theme.icon.inverse};

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      background: ${({ theme }) => theme.background.default};
    }
  }

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.default};
  }

  &:disabled {
    color: ${({ theme }) => theme.icon.disabledSubtle};
  }
`;
